import { trackCtaClick } from '#utils/utils';
import {
  Badge,
  Box,
  Button,
  Container,
  HStack,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  List,
  ListIcon,
  ListItem,
  SimpleGrid,
  Stack,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import React, { useState } from 'react';
import { FaCheckCircle, FaLock, FaRobot } from 'react-icons/fa/index.js';
import { MdSearch } from 'react-icons/md/index.js';

const MotionBox = motion(Box);

// Questions staff ask every day
const COMMON_QUESTIONS = [
  "What's the WiFi password for the conference room?",
  "How many PTO days do I have left?",
  "What's the process for onboarding a new client?",
  "Where is the template for the service agreement?",
  "Who do I contact for IT issues after hours?",
  "What's our HIPAA data handling policy?",
  "How do I submit an expense report?",
  "What's the phone number for our benefits provider?",
];

const USE_CASES = [
  {
    title: 'Employee Onboarding',
    desc: 'New hires ask the assistant instead of interrupting HR. Policies, forms, tools, and processes — all answered instantly.',
    icon: '👋',
  },
  {
    title: 'Policy & Compliance',
    desc: 'Staff get accurate, up-to-date answers on HIPAA, PTO, expense policies, and procedures without digging through shared drives.',
    icon: '📋',
  },
  {
    title: 'Process Documentation',
    desc: 'Every workflow, checklist, and SOP lives inside the assistant. The process changes once — every answer updates automatically.',
    icon: '⚙️',
  },
  {
    title: 'IT & Operations',
    desc: 'WiFi passwords, software licenses, vendor contacts, escalation paths — your team gets answers in seconds, not tickets.',
    icon: '🖥️',
  },
];

const HOW_IT_WORKS = [
  { step: '01', title: 'Knowledge Audit', desc: 'We inventory every document, policy, process, and piece of institutional knowledge your business relies on.' },
  { step: '02', title: 'Ingest & Index', desc: 'Documents are ingested, chunked, and indexed into a vector database — your knowledge becomes searchable by meaning, not just keywords.' },
  { step: '03', title: 'Assistant Deployment', desc: 'We deploy a private AI assistant trained on your knowledge — accessible via web, Slack, Teams, or embedded in your tools.' },
  { step: '04', title: 'Training & Handoff', desc: 'Your team learns how to query, how to add new knowledge, and how to manage the assistant without technical help.' },
];

const INDUSTRIES = [
  { name: 'Healthcare & Dental', note: 'HIPAA-aware. Data stays within your environment.' },
  { name: 'Architecture & Engineering', note: 'Project specs, vendor contacts, and process docs — instantly accessible.' },
  { name: 'Education', note: 'Staff handbook, policies, and classroom procedures in one place.' },
  { name: 'Nonprofit', note: 'Donor management SOPs, grant processes, and compliance docs.' },
  { name: 'Professional Services', note: 'Client playbooks, engagement templates, and billing procedures.' },
];

// Fake chat demo
const DEMO_MESSAGES = [
  { role: 'user', text: "What's our policy on remote work?" },
  { role: 'assistant', text: "According to your Employee Handbook (updated March 2024): Staff may work remotely up to 3 days per week with manager approval. Core hours are 10am–3pm in the local timezone. Equipment requests go through IT using the Remote Work Request form in the portal." },
];

const KnowledgeAssistantPage = () => {
  const [demoInput, setDemoInput] = useState('');
  const pageBg = useColorModeValue('gray.50', 'gray.950');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const headingColor = useColorModeValue('gray.900', 'white');
  const chatBg = useColorModeValue('gray.100', 'gray.900');
  const userBubble = useColorModeValue('blue.500', 'blue.400');
  const aiBubble = useColorModeValue('white', 'gray.700');
  const aiBubbleBorder = useColorModeValue('gray.200', 'gray.600');

  return (
    <Box bg={pageBg}>
      {/* Hero */}
      <Box
        bgGradient="linear(to-br, purple.600, pink.500)"
        py={{ base: 20, md: 32 }}
        position="relative"
        overflow="hidden"
      >
        <Box position="absolute" top="-80px" right="-80px" w="400px" h="400px" borderRadius="full" bg="whiteAlpha.100" pointerEvents="none" />
        <Box position="absolute" bottom="-60px" left="-60px" w="300px" h="300px" borderRadius="full" bg="whiteAlpha.50" pointerEvents="none" />

        <Container maxW="container.xl">
          <Stack direction={{ base: 'column', lg: 'row' }} spacing={12} align="center">
            <MotionBox
              flex="1"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 } as any}
            >
              <Badge bg="whiteAlpha.200" color="white" fontSize="sm" px={4} py={1} borderRadius="full" mb={4} display="inline-block">
                Solution 2 of 3
              </Badge>
              <Heading
                as="h1"
                fontSize={{ base: '3xl', md: '5xl' }}
                fontWeight="extrabold"
                color="white"
                lineHeight="1.1"
                letterSpacing="-0.03em"
                mb={6}
              >
                Internal Knowledge
                <br />
                Assistant
              </Heading>
              <Text fontSize="xl" color="whiteAlpha.800" mb={8} maxW="lg">
                One AI assistant. All your company knowledge.
                Your team stops searching shared drives, asking HR, or waiting
                for an email reply — and gets answers in seconds.
              </Text>
              <HStack spacing={4} flexWrap="wrap">
                <Button
                  size="lg" bg="white" color="purple.600" fontWeight="bold" px={8}
                  as="a" href="/contact-us?interest=knowledge-assistant"
                  onClick={() => trackCtaClick({ cta_label: 'Build My Knowledge Assistant', cta_location: 'KnowledgeAssistant Hero', destination_url: '/contact-us?interest=knowledge-assistant' })}
                  _hover={{ bg: 'gray.50', transform: 'translateY(-2px)', boxShadow: 'xl' }}
                  transition="all 0.2s"
                >
                  Build My Knowledge Assistant
                </Button>
                <Button
                  size="lg" variant="outline" borderColor="whiteAlpha.600" color="white" fontWeight="bold" px={8}
                  as="a" href="/pricing"
                  _hover={{ bg: 'whiteAlpha.100', transform: 'translateY(-2px)' }}
                  transition="all 0.2s"
                >
                  See Pricing
                </Button>
              </HStack>
            </MotionBox>

            {/* Live Chat Demo UI */}
            <MotionBox
              flex="0 0 auto" w={{ base: 'full', md: '380px' }}
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.2 } as any}
            >
              <Box
                bg={aiBubble}
                border="1px solid"
                borderColor="whiteAlpha.300"
                borderRadius="2xl"
                overflow="hidden"
                boxShadow="2xl"
              >
                {/* Chat header */}
                <Box bg="purple.700" px={5} py={3}>
                  <HStack spacing={2}>
                    <Icon as={FaRobot} color="white" boxSize={4} />
                    <Text fontSize="sm" fontWeight="bold" color="white">TenK Assistant</Text>
                    <Badge bg="green.400" color="white" fontSize="2xs" ml="auto" borderRadius="full" px={2}>Online</Badge>
                  </HStack>
                </Box>
                {/* Messages */}
                <Box bg={chatBg} p={4} minH="180px">
                  <VStack spacing={3} align="stretch">
                    {DEMO_MESSAGES.map((msg, i) => (
                      <MotionBox
                        key={i}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 + i * 0.4 } as any}
                        display="flex"
                        justifyContent={msg.role === 'user' ? 'flex-end' : 'flex-start'}
                      >
                        <Box
                          maxW="85%"
                          bg={msg.role === 'user' ? userBubble : aiBubble}
                          color={msg.role === 'user' ? 'white' : headingColor}
                          border={msg.role === 'assistant' ? '1px solid' : 'none'}
                          borderColor={aiBubbleBorder}
                          borderRadius={msg.role === 'user' ? '2xl 2xl 0 2xl' : '2xl 2xl 2xl 0'}
                          px={4} py={3}
                          fontSize="xs"
                          lineHeight="1.5"
                        >
                          {msg.text}
                        </Box>
                      </MotionBox>
                    ))}
                  </VStack>
                </Box>
                {/* Input */}
                <Box bg={aiBubble} p={3} borderTop="1px solid" borderColor={borderColor}>
                  <InputGroup size="sm">
                    <InputLeftElement pointerEvents="none">
                      <Icon as={MdSearch} color="gray.400" />
                    </InputLeftElement>
                    <Input
                      placeholder="Ask your assistant anything..."
                      value={demoInput}
                      onChange={(e) => setDemoInput(e.target.value)}
                      borderRadius="full"
                      bg={chatBg}
                      border="none"
                      _focus={{ boxShadow: 'none', bg: chatBg }}
                      fontSize="xs"
                    />
                  </InputGroup>
                </Box>
              </Box>
              <Text fontSize="xs" color="whiteAlpha.600" textAlign="center" mt={2}>
                Your data stays in your environment.
              </Text>
            </MotionBox>
          </Stack>
        </Container>
      </Box>

      {/* Common Questions */}
      <Box py={{ base: 16, md: 24 }}>
        <Container maxW="container.xl">
          <VStack spacing={4} mb={14} textAlign="center">
            <Heading as="h2" fontSize={{ base: '2xl', md: '3xl' }} fontWeight="extrabold" color={headingColor} letterSpacing="-0.02em">
              The questions your team asks every single day.
            </Heading>
            <Text fontSize="lg" color={textColor} maxW="xl">
              Each one interrupts someone's workflow. Each one costs time and focus.
              The assistant answers all of them — instantly, accurately, 24/7.
            </Text>
          </VStack>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
            {COMMON_QUESTIONS.map((q, i) => (
              <MotionBox
                key={q}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.07 } as any}
                bg={cardBg}
                border="1px solid"
                borderColor={borderColor}
                borderRadius="xl"
                p={5}
                boxShadow="sm"
                _hover={{ boxShadow: 'md', borderColor: 'purple.300' }}
                style={{ transition: 'all 0.2s ease' }}
              >
                <Text fontSize="sm" color={headingColor} fontStyle="italic" lineHeight="1.6">
                  &ldquo;{q}&rdquo;
                </Text>
              </MotionBox>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* Use Cases */}
      <Box bg={useColorModeValue('purple.50', 'purple.950')} py={{ base: 16, md: 24 }}>
        <Container maxW="container.xl">
          <VStack spacing={4} mb={14} textAlign="center">
            <Badge colorScheme="purple" fontSize="sm" px={4} py={1} borderRadius="full">Use Cases</Badge>
            <Heading as="h2" fontSize={{ base: '2xl', md: '3xl' }} fontWeight="extrabold" color={headingColor} letterSpacing="-0.02em">
              One assistant. Unlimited applications.
            </Heading>
          </VStack>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
            {USE_CASES.map((uc, i) => (
              <MotionBox
                key={uc.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 } as any}
                bg={cardBg}
                border="1px solid"
                borderColor={borderColor}
                borderRadius="2xl"
                p={8}
                boxShadow="md"
              >
                <Text fontSize="3xl" mb={3}>{uc.icon}</Text>
                <Heading as="h3" fontSize="xl" fontWeight="bold" color={headingColor} mb={3}>
                  {uc.title}
                </Heading>
                <Text fontSize="md" color={textColor} lineHeight="1.7">
                  {uc.desc}
                </Text>
              </MotionBox>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* How it works */}
      <Box py={{ base: 16, md: 24 }}>
        <Container maxW="container.xl">
          <VStack spacing={4} mb={14} textAlign="center">
            <Badge colorScheme="purple" fontSize="sm" px={4} py={1} borderRadius="full">The Process</Badge>
            <Heading as="h2" fontSize={{ base: '2xl', md: '3xl' }} fontWeight="extrabold" color={headingColor} letterSpacing="-0.02em">
              From scattered documents to instant answers.
            </Heading>
          </VStack>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
            {HOW_IT_WORKS.map((h, i) => (
              <MotionBox
                key={h.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 } as any}
                bg={cardBg}
                border="1px solid"
                borderColor={borderColor}
                borderRadius="xl"
                p={7}
                boxShadow="sm"
                _hover={{ borderColor: 'purple.300', transform: 'translateY(-4px)' }}
                style={{ transition: 'all 0.3s ease' }}
              >
                <Text fontSize="3xl" fontWeight="extrabold" color="purple.400" fontFamily="mono" mb={3}>{h.step}</Text>
                <Heading as="h3" fontSize="md" fontWeight="bold" color={headingColor} mb={2}>{h.title}</Heading>
                <Text fontSize="sm" color={textColor} lineHeight="1.6">{h.desc}</Text>
              </MotionBox>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* Industries + Security */}
      <Box bg={useColorModeValue('gray.900', 'gray.950')} py={{ base: 16, md: 24 }}>
        <Container maxW="container.xl">
          <Stack direction={{ base: 'column', lg: 'row' }} spacing={16}>
            <Box flex="1">
              <Badge colorScheme="purple" mb={4} fontSize="sm" px={3} py={1} borderRadius="full">Industries</Badge>
              <Heading as="h2" fontSize={{ base: 'xl', md: '2xl' }} fontWeight="extrabold" color="white" mb={6}>
                Built for regulated, document-heavy industries.
              </Heading>
              <List spacing={4}>
                {INDUSTRIES.map((ind) => (
                  <ListItem key={ind.name}>
                    <HStack align="flex-start" spacing={3}>
                      <ListIcon as={FaCheckCircle} color="purple.400" mt={0.5} />
                      <Box>
                        <Text fontWeight="bold" color="white" fontSize="sm">{ind.name}</Text>
                        <Text fontSize="xs" color="gray.400">{ind.note}</Text>
                      </Box>
                    </HStack>
                  </ListItem>
                ))}
              </List>
            </Box>
            <Box flex="1">
              <Badge colorScheme="green" mb={4} fontSize="sm" px={3} py={1} borderRadius="full">Security</Badge>
              <Heading as="h2" fontSize={{ base: 'xl', md: '2xl' }} fontWeight="extrabold" color="white" mb={6}>
                Your data never leaves your environment.
              </Heading>
              <VStack spacing={4} align="stretch">
                {[
                  { icon: FaLock, title: 'Private deployment', desc: 'Your knowledge base runs in an isolated environment — not a shared SaaS instance.' },
                  { icon: FaLock, title: 'HIPAA-aware architecture', desc: 'Built with healthcare data handling requirements in mind from day one.' },
                  { icon: FaLock, title: 'Role-based access', desc: 'Control exactly who can query what. Executives see everything. Staff see what they need.' },
                ].map((item) => (
                  <HStack
                    key={item.title}
                    spacing={4}
                    bg="gray.800"
                    borderRadius="xl"
                    p={5}
                    border="1px solid"
                    borderColor="gray.700"
                  >
                    <Icon as={item.icon} color="green.400" boxSize={5} flexShrink={0} />
                    <Box>
                      <Text fontWeight="bold" color="white" fontSize="sm">{item.title}</Text>
                      <Text fontSize="xs" color="gray.400">{item.desc}</Text>
                    </Box>
                  </HStack>
                ))}
              </VStack>
            </Box>
          </Stack>
        </Container>
      </Box>

      {/* CTA */}
      <Box bgGradient="linear(to-r, purple.600, pink.500)" py={{ base: 16, md: 20 }}>
        <Container maxW="container.md" textAlign="center">
          <Heading as="h2" fontSize={{ base: '2xl', md: '4xl' }} fontWeight="extrabold" color="white" mb={4} letterSpacing="-0.02em">
            Ready to stop answering the same questions twice?
          </Heading>
          <Text fontSize="lg" color="whiteAlpha.800" mb={8}>
            Book a free discovery call. We'll audit your knowledge base and
            show you exactly what the assistant will cover.
          </Text>
          <HStack spacing={4} justify="center" flexWrap="wrap">
            <Button
              size="lg" bg="white" color="purple.600" fontWeight="bold" px={10}
              as="a" href="/contact-us?interest=knowledge-assistant&package=launch"
              onClick={() => trackCtaClick({ cta_label: 'Build My Knowledge Assistant CTA', cta_location: 'KnowledgeAssistant CTA', destination_url: '/contact-us?interest=knowledge-assistant' })}
              _hover={{ bg: 'gray.50', transform: 'translateY(-2px)', boxShadow: 'xl' }}
              transition="all 0.2s"
            >
              Build My Knowledge Assistant
            </Button>
            <Button
              size="lg" variant="outline" borderColor="whiteAlpha.600" color="white" px={8}
              as="a" href="/pricing"
              _hover={{ bg: 'whiteAlpha.100' }}
            >
              Starting at $3,000
            </Button>
          </HStack>
        </Container>
      </Box>
    </Box>
  );
};

export default KnowledgeAssistantPage;
